/**
 * Graph-backed tag picker — the shared identity front-door for
 * every surface that selects tags (config forms, dashboard editor).
 *
 * Search is a debounced inventory read over graph `:Tag` nodes, so it covers
 * every vendor — Ignition, AVEVA,
 * plain OPC — including tags that have no Postgres registry row at all.
 * It is deliberately NOT backed by the registry list endpoint.
 *
 * The picker is suggestion + validation, not an allow-list: with
 * `allowCustomValue` (the default for config forms) a path that isn't in
 * the graph can still be committed verbatim.
 */
import { useMemo, useState } from 'react'
import { CheckIcon, ChevronsUpDownIcon, Loader2, PlusIcon, XIcon } from 'lucide-react'
import {
  useTagInventorySearchQuery,
  type TagInventoryTag
} from '@axilon-apps/domains-ontology-ui-state'

import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '../ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { formatTagShort } from '../../pages/ConfigurePage/util/tag-paths'
import { cn } from '../../lib/utils'
import { useDebouncedValue } from '../../lib/use-debounced-value'
import { TAG_VENDOR_LABEL } from './tag-vendor'

export interface TagPickerProps {
  /** Selected tag paths (graph-canonical strings). */
  value: string[]
  onChange: (paths: string[]) => void
  /** Called with the graph URN when a search result is added. */
  onPick?: (nodeId: string) => void
  /**
   * Permit committing a search string that isn't a graph match. The picker
   * is a suggestion surface, not an allow-list — keep this on for config
   * forms so unmodeled tags can still be configured.
   */
  allowCustomValue?: boolean
  /** Close the popover after each pick (single-select flows). */
  closeOnSelect?: boolean
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  /** Prefix for DOM ids/testids so multiple pickers coexist on a page. */
  idPrefix?: string
}

export function TagPicker({
  value,
  onChange,
  onPick,
  allowCustomValue = false,
  closeOnSelect = false,
  placeholder = 'Search tags…',
  disabled,
  invalid,
  idPrefix = 'tag-picker'
}: TagPickerProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 200)
  const searchIsSettled = search.trim() === debouncedSearch.trim()

  const { data, isLoading, error } = useTagInventorySearchQuery(
    { query: debouncedSearch, pageSize: 50 },
    { skip: !open || !searchIsSettled || debouncedSearch.trim() === '' }
  )

  const selected = useMemo(() => new Set(value), [value])
  const results = useMemo(
    () => (searchIsSettled ? (data?.results ?? []).map((hit) => hit.tag) : []),
    [data?.results, searchIsSettled]
  )
  const isSearching = !searchIsSettled || isLoading

  const trimmedSearch = search.trim()
  const showCustomRow =
    allowCustomValue &&
    trimmedSearch.length > 0 &&
    !selected.has(trimmedSearch) &&
    !results.some((r) => r.tagPath === trimmedSearch)

  const addPath = (path: string, record?: TagInventoryTag) => {
    const p = path.trim()
    if (!p || selected.has(p)) return
    onChange([...value, p])
    if (record?.nodeId) onPick?.(record.nodeId)
    setSearch('')
    if (closeOnSelect) setOpen(false)
  }

  const removePath = (path: string) => {
    onChange(value.filter((p) => p !== path))
  }

  return (
    <div className="flex flex-col gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-invalid={invalid ? true : undefined}
            disabled={disabled}
            className={cn(
              'w-full justify-between font-normal text-ink-2',
              invalid && 'border-st-down'
            )}
            data-testid={`${idPrefix}-trigger`}
          >
            {value.length > 0
              ? `${value.length} tag${value.length === 1 ? '' : 's'} selected`
              : placeholder}
            <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[26rem] p-0">
          {/* Server-side inventory search — disable cmdk's client-side filter. */}
          <Command shouldFilter={false}>
            <CommandInput
              value={search}
              onValueChange={setSearch}
              placeholder="Type to search the ontology…"
              data-testid={`${idPrefix}-input`}
            />
            <CommandList data-testid={`${idPrefix}-list`}>
              {isSearching ? (
                <div className="flex items-center gap-2 px-3 py-2.5 text-12 text-ink-3">
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                  Searching tags…
                </div>
              ) : null}
              {searchIsSettled && error ? (
                <div className="px-3 py-2.5 text-12 text-st-down">Tag search failed: {error}</div>
              ) : null}
              {!isSearching && !error ? (
                <CommandEmpty>
                  {allowCustomValue
                    ? 'No matching tags — press the row below to add the path as typed.'
                    : 'No matching tags.'}
                </CommandEmpty>
              ) : null}
              {showCustomRow ? (
                <CommandGroup>
                  <CommandItem
                    value={`__custom__${trimmedSearch}`}
                    onSelect={() => addPath(trimmedSearch)}
                    data-testid={`${idPrefix}-custom`}
                  >
                    <PlusIcon className="size-3.5" aria-hidden />
                    <span className="truncate font-mono text-12">Use “{trimmedSearch}”</span>
                  </CommandItem>
                </CommandGroup>
              ) : null}
              {results.length > 0 ? (
                <CommandGroup>
                  {results.map((tag) => {
                    const isSelected = selected.has(tag.tagPath)
                    const vendorLabel = TAG_VENDOR_LABEL[tag.vendor]
                    return (
                      <CommandItem
                        key={tag.nodeId || tag.tagPath}
                        value={tag.tagPath}
                        onSelect={() => {
                          if (isSelected) removePath(tag.tagPath)
                          else addPath(tag.tagPath, tag)
                        }}
                        data-testid={`${idPrefix}-option`}
                      >
                        <CheckIcon
                          className={cn('size-3.5', isSelected ? 'opacity-100' : 'opacity-0')}
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1 truncate font-mono text-12">
                          {tag.tagPath}
                        </span>
                        {vendorLabel ? (
                          <span className="ml-2 shrink-0 rounded bg-bg-sunk px-1.5 py-0.5 text-10 font-semibold text-ink-3">
                            {vendorLabel}
                          </span>
                        ) : null}
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-row flex-wrap gap-1.5" data-testid={`${idPrefix}-selected`}>
          {value.map((tp) => (
            <Badge key={tp} variant="secondary" className="gap-1 pr-1" title={tp}>
              <span className="max-w-[18rem] truncate">{formatTagShort(tp)}</span>
              <button
                type="button"
                onClick={() => removePath(tp)}
                className="ml-1 inline-flex size-4 items-center justify-center rounded-full hover:bg-destructive/20"
                aria-label={`Remove ${tp}`}
              >
                <XIcon className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}

export default TagPicker
